import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Facebook, Linkedin, Mail, LifeBuoy, Apple, Play, Check } from "lucide-react";
import { Logo } from "./Logo";
import { BRAND } from "../data/content";
import { track } from "../lib/analytics";

const FooterNewsletter = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await fetch(`${API}/api/newsletter-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "footer" }),
      });
      track("newsletter_signup", "footer");
    } catch (err) { /* noop */ }
    setDone(true);
  };
  return (
    <div className="mt-12 rounded-3xl border border-line bg-white/70 p-6 sm:p-8" data-testid="footer-newsletter">
      <div className="grid gap-5 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-sage-deep">A little HEARD in your inbox</p>
          <p className="mt-1.5 text-sm leading-relaxed text-ink/70">Thoughtful resources, encouragement, and things worth knowing — delivered gently.</p>
        </div>
        {done ? (
          <div className="flex items-center gap-2 text-sm text-sage-deep" data-testid="footer-newsletter-success">
            <Check size={18} /> Thank you — a little HEARD is on its way. 🌿
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" data-testid="footer-newsletter-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="w-full flex-1 rounded-full border border-line bg-white px-5 py-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-sage"
              data-testid="footer-newsletter-input"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-sage-deep px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-cream transition-colors hover:bg-sage" data-testid="footer-newsletter-submit">
              Subscribe
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Mail, label: "Email", href: `mailto:${BRAND.email}` },
];

export const Footer = () => (
  <footer className="relative bg-cream-deep border-t border-line" data-testid="footer">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-16">
      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.1fr]">
        <div>
          <Logo tagline />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-ink/70">
            A community and everyday-life resource built on empathy, encouragement, and shared experiences — for every season of life.
          </p>
          <p className="mt-4 text-sm font-medium text-sage-deep">{BRAND.domain}</p>
          <div className="mt-6 flex items-center gap-3.5" data-testid="footer-socials">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer noopener" aria-label={s.label}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-sage-deep transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage hover:text-white hover:border-sage hover:shadow-[0_8px_20px_rgba(126,154,107,0.28)]">
                <s.icon size={19} />
              </a>
            ))}
          </div>
        </div>

        <FooterCol title="Explore" links={[
          { label: "Home", to: "/" },
          { label: "Explore", to: "/explore" },
          { label: "Everyday Resources", to: "/resources" },
          { label: "What We're Learning", to: "/learning" },
          { label: "HEARD Finds", to: "/finds" },
          { label: "Community", to: "/community" },
        ]} />
        <FooterCol title="Company" links={[
          { label: "About HEARD", to: "/about" },
          { label: "Support Center", to: "/support" },
          { label: "Contact", to: "/contact" },
        ]} />
        <FooterCol title="Policies & Safety" links={[
          { label: "Privacy Policy", to: "/privacy" },
          { label: "Terms of Use", to: "/terms" },
          { label: "Community Guidelines", to: "/community-guidelines" },
          { label: "Child Safety Standards", to: "/child-safety" },
          { label: "Account Deletion", to: "/delete-account" },
        ]} />

        <div className="rounded-3xl bg-white border border-line p-6 shadow-[0_8px_30px_rgba(47,75,57,0.05)]">
          <div className="flex items-center gap-2 text-sage-deep">
            <LifeBuoy size={18} />
            <span className="font-serif text-xl font-semibold">Get the app</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink/70">Launching soon on your favorite store.</p>
          <div className="mt-4 flex flex-col gap-2.5">
            <StoreBadge icon={Apple} title="App Store" />
            <StoreBadge icon={Play} title="Google Play" />
          </div>
        </div>
      </div>

      <FooterNewsletter />

      <div className="mt-14 pt-6 border-t border-line flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-ink/55">© 2026 {BRAND.legal}, LLC. All rights reserved.</p>
        <p className="text-xs text-ink/45">Built with empathy.</p>
      </div>
    </div>
  </footer>
);

const StoreBadge = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 rounded-xl border border-line bg-cream/60 px-4 py-2.5 opacity-80" title="Coming soon" data-testid={`store-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon size={20} className="text-sage-deep" />
    <div className="leading-tight">
      <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ink/50">Coming soon</p>
      <p className="text-sm font-medium text-charcoal">{title}</p>
    </div>
  </div>
);

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="mb-4 text-xs uppercase tracking-[0.2em] text-sage font-medium">{title}</h4>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.to + l.label}>
          <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} className="text-sm text-ink/70 transition-colors hover:text-sage-deep">{l.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);
